import { FormEvent, useEffect, useState } from 'react';
import {
  createCandidate,
  updateCandidate,
  Vacancy,
} from '../../../../network/ats';
import {
  Modal,
  errorMessage,
  fieldClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
} from './Modal';

export type PipelineCandidate = {
  id: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  location: string;
  description: string;
  jobId: string;
  stage: string;
  dateCreated?: string;
  resumeUrl?: string;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  vacancies: Vacancy[];
  /** Edit this candidate; add a new one when absent. */
  candidate?: PipelineCandidate | null;
  defaultVacancyId?: string;
};

const EMPTY = {
  name: '',
  email: '',
  phone: '',
  vacancyId: '',
  position: '',
  location: '',
  description: '',
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const CandidateFormDialog = ({
  isOpen,
  onClose,
  onSaved,
  vacancies,
  candidate,
  defaultVacancyId,
}: Props) => {
  const isEdit = Boolean(candidate);
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setValues(
      candidate
        ? {
            name: candidate.name,
            email: candidate.email,
            phone: candidate.phone,
            vacancyId: candidate.jobId,
            position: candidate.position,
            location: candidate.location,
            description: candidate.description,
          }
        : { ...EMPTY, vacancyId: defaultVacancyId ?? '' },
    );
  }, [isOpen, candidate, defaultVacancyId]);

  const set =
    (field: keyof typeof EMPTY) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) =>
      setValues((prev) => ({ ...prev, [field]: event.target.value }));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const name = values.name.trim();
    const email = values.email.trim();
    if (!name) return setError('Name is required.');
    if (email && !EMAIL.test(email))
      return setError('Enter a valid email address.');
    if (!values.vacancyId && !values.position.trim())
      return setError('Choose a vacancy or enter a position.');

    setIsSaving(true);
    setError(null);
    try {
      if (candidate) {
        await updateCandidate(candidate.id, {
          name,
          email,
          phone: values.phone.trim(),
          position: values.position.trim(),
          location: values.location.trim(),
          description: values.description.trim(),
          ...(values.vacancyId ? { jobId: values.vacancyId } : {}),
        });
      } else {
        await createCandidate({
          name,
          ...(email ? { email } : {}),
          ...(values.phone.trim() ? { phone: values.phone.trim() } : {}),
          ...(values.vacancyId ? { vacancyId: values.vacancyId } : {}),
          ...(values.position.trim()
            ? { position: values.position.trim() }
            : {}),
          location: values.location.trim(),
          description: values.description.trim(),
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(
        errorMessage(
          err,
          isEdit ? 'Could not save the candidate.' : 'Could not add the candidate.',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit candidate' : 'Add candidate'}
      description={
        isEdit ? undefined : 'Add someone who applied outside the job portal.'
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <div>
          <label htmlFor="candidate-name" className={labelClass}>
            Full name *
          </label>
          <input
            id="candidate-name"
            className={fieldClass}
            value={values.name}
            onChange={set('name')}
            autoComplete="off"
            required
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="candidate-email" className={labelClass}>
              Email
            </label>
            <input
              id="candidate-email"
              type="email"
              className={fieldClass}
              value={values.email}
              onChange={set('email')}
            />
          </div>
          <div>
            <label htmlFor="candidate-phone" className={labelClass}>
              Phone
            </label>
            <input
              id="candidate-phone"
              type="tel"
              className={fieldClass}
              value={values.phone}
              onChange={set('phone')}
            />
          </div>
        </div>
        <div>
          <label htmlFor="candidate-vacancy" className={labelClass}>
            Vacancy
          </label>
          <select
            id="candidate-vacancy"
            className={fieldClass}
            value={values.vacancyId}
            onChange={set('vacancyId')}
          >
            <option value="">No vacancy</option>
            {vacancies.map((vacancy) => (
              <option key={vacancy.id} value={vacancy.id}>
                {vacancy.jobTitle}
                {vacancy.status === 'closed' ? ' (closed)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="candidate-position" className={labelClass}>
              Position{values.vacancyId ? '' : ' *'}
            </label>
            <input
              id="candidate-position"
              className={fieldClass}
              value={values.position}
              onChange={set('position')}
              placeholder={values.vacancyId ? 'Defaults to the vacancy title' : ''}
            />
          </div>
          <div>
            <label htmlFor="candidate-location" className={labelClass}>
              Location
            </label>
            <input
              id="candidate-location"
              className={fieldClass}
              value={values.location}
              onChange={set('location')}
            />
          </div>
        </div>
        <div>
          <label htmlFor="candidate-notes" className={labelClass}>
            Notes
          </label>
          <textarea
            id="candidate-notes"
            rows={3}
            className={fieldClass}
            value={values.description}
            onChange={set('description')}
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            className={secondaryButtonClass}
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button type="submit" className={primaryButtonClass} disabled={isSaving}>
            {isSaving ? 'Saving…' : isEdit ? 'Save changes' : 'Add candidate'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
